import { getFCMToken, initializeFirebaseMessaging, waitForServiceWorkerActive, setupForegroundMessageListener } from '../config/firebase';
import agent from './api';
import { getFCMTokenCookie, setFCMTokenCookie, removeFCMTokenCookie } from '../utils/fcmCookieUtils';
import { vendorDataResponse } from '../models/axiosModel';

interface FCMRegisterTokenPayload {
  fcm_token_value: string;
}


/**
 * Register service worker for FCM
 * Note: Service worker file must exist at /firebase-messaging-sw.js
 * This cannot be created dynamically due to browser security restrictions
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    console.warn('[FCM] Service Worker not supported in this browser');
    return null;
  }

  try {
    console.log('[FCM] Attempting to register/check service worker...');
    
    // First, wait for service worker to be ready (this ensures any pending registrations complete)
    // This is important if service worker was registered in index.js
    try {
      await navigator.serviceWorker.ready;
      console.log('[FCM] Service Worker ready');
    } catch (swError) {
      console.warn('[FCM] Service Worker ready check failed, continuing anyway:', swError);
    }
    
    // Check if we already have a service worker registered
    let registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    
    if (!registration) {
      // Register the service worker file
      console.log('[FCM] No existing registration found, registering new service worker...');
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/'
      });
      console.log('[FCM] Service Worker registered:', registration);
    } else {
      console.log('[FCM] Service Worker already registered, checking state...');
      console.log('[FCM] Registration state:', {
        active: registration.active?.state,
        installing: registration.installing?.state,
        waiting: registration.waiting?.state
      });
      
      // If there's an update waiting, skip waiting and use current active one
      if (registration.waiting) {
        console.log('[FCM] Service worker update available, but using current active version');
      }
    }

    // Wait for service worker to be active (with timeout handling)
    console.log('[FCM] Waiting for service worker to be active...');
    const activeRegistration = await waitForServiceWorkerActive('/firebase-messaging-sw.js');
    
    if (!activeRegistration) {
      console.error('[FCM] Service worker is not active after waiting');
      return null;
    }
    
    if (!activeRegistration.active || activeRegistration.active.state !== 'activated') {
      console.error('[FCM] Service worker is not in activated state:', activeRegistration.active?.state);
      return null;
    }
    
    console.log('[FCM] Service worker is active and ready');
    return activeRegistration;
  } catch (error: any) {
    console.error('[FCM] Error registering service worker:', error);
    console.error('[FCM] Error details:', error?.message);
    console.error('[FCM] Error stack:', error?.stack);
    console.error('[FCM] Make sure public/firebase-messaging-sw.js exists');
    return null;
  }
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('[FCM] Notifications not supported in this browser');
    return 'denied';
  }

  // Check current permission
  if (Notification.permission === 'granted') {
    console.log('[FCM] Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('[FCM] Notification permission denied');
    return 'denied';
  }

  // Request permission
  try {
    const permission = await Notification.requestPermission();
    console.log('[FCM] Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('[FCM] Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Check if FCM token is valid by calling backend API
 */
export const checkFCMTokenValidity = async (token: string): Promise<boolean> => {
  try {
    const response = await agent.checkFcmToken.get(token);
    
    if (response.status) {
      console.log('[FCM] Token is valid');
      return true;
    } else {
      console.log('[FCM] Token is invalid or not found');
      return false;
    }
  } catch (error: any) {
    console.error('[FCM] Error checking FCM token validity:', error);
    // If API call fails, assume token is invalid to be safe
    return false;
  }
};

/**
 * Register FCM token with backend
 */
export const registerFCMTokenWithBackend = async (
  token: string
): Promise<boolean> => {
  try {
    const response = await agent.fcmRegisterToken.post({ fcm_token_value: token });
    
    if (response.status) {
      console.log('[FCM] Token registered successfully with backend');
      return true;
    } else {
      // Check if error is because token already exists
      const errorMsg = response.error?.message || response?.error || '';
      if (errorMsg.includes('Already Exists') || errorMsg.includes('already exists')) {
        console.log('[FCM] Token already exists in database. This is fine - token is already registered.');
        return true; // Consider this success since token exists
      } else {
        console.error('[FCM] Failed to register FCM token:', errorMsg);
        return false;
      }
    }
  } catch (error: any) {
    console.error('[FCM] Error registering FCM token with backend:', error);
    return false;
  }
};

// Track if foreground listener is already set up to prevent duplicates
let foregroundListenerSetup = false;
let foregroundUnsubscribe: (() => void) | null = null;
// Track shown notifications to prevent duplicates (using message ID or timestamp)
const shownNotificationIds = new Set<string>();

/**
 * Check if page is currently visible
 * Note: We only check visibilityState, not hasFocus(), because hasFocus() can be false
 * even when page is visible (e.g., user clicked on an input field)
 */
const isPageVisible = (): boolean => {
  const isVisible = document.visibilityState === 'visible';
  return isVisible;
};

/**
 * Generate a unique ID for the notification to prevent duplicates
 */
const getNotificationId = (payload: any): string => {
  // Use messageId if available, otherwise use a combination of title + body + timestamp
  return payload.messageId || 
         payload.data?.messageId || 
         `${payload.notification?.title || ''}_${payload.notification?.body || ''}_${Date.now()}` ||
         `fcm_${Date.now()}`;
};

/**
 * Setup foreground message listener for notifications when app is open
 * This must be called after Firebase Messaging is initialized
 * Note: Firebase's onMessage automatically prevents duplicate listeners, but we track it anyway
 */
export const setupForegroundNotificationListener = async (): Promise<void> => {
  try {
    // Ensure Firebase Messaging is initialized first
    let messagingInstance = await initializeFirebaseMessaging();
    if (!messagingInstance) {
      console.error('[FCM] Cannot setup foreground listener - Firebase Messaging not initialized');
      foregroundListenerSetup = false;
      return;
    }

    // If listener is already setup, we should clean up the old one first
    // Firebase's onMessage can have multiple listeners, but we want to ensure only one active listener
    if (foregroundListenerSetup && foregroundUnsubscribe) {
      console.log('[FCM] Foreground listener already setup, cleaning up old listener...');
      try {
        foregroundUnsubscribe();
        foregroundUnsubscribe = null;
        foregroundListenerSetup = false;
      } catch (error) {
        console.warn('[FCM] Error cleaning up old listener:', error);
      }
    }

    // Setup foreground message listener (for when app is open)
    // This handles notifications when user is actively viewing the page
    const unsubscribe = setupForegroundMessageListener(async (payload) => {
      console.log('[FCM] 📱 Foreground message received:', payload);
      
      // Get unique notification ID to prevent duplicates
      const notificationId = getNotificationId(payload);
      
      // Check if we've already shown this notification (prevent duplicates)
      if (shownNotificationIds.has(notificationId)) {
        console.log('[FCM] ⚠️ Duplicate notification detected, skipping:', notificationId);
        return;
      }
      
      // Check if page is visible
      const pageVisible = isPageVisible();
      const hasFocus = document.hasFocus();
      
      console.log('[FCM] Page visibility state:', {
        visibilityState: document.visibilityState,
        hasFocus: hasFocus,
        isVisible: pageVisible,
        willShowNotification: pageVisible
      });
      
      // Only show notification in foreground listener if page is visible
      // When page is not visible (hidden tab), the service worker will handle it (preventing duplicates)
      if (!pageVisible) {
        console.log('[FCM] Page is not visible (hidden tab), letting service worker handle notification');
        // Don't show notification here - service worker will handle it
        return;
      }
      
      // Page is visible - we should show notification
      console.log('[FCM] Page is visible, showing foreground notification');
      
      // Page is visible - show notification via foreground listener
      // Check notification permission
      if (Notification.permission !== 'granted') {
        console.warn('[FCM] ⚠️ Notification permission not granted. Current permission:', Notification.permission);
        console.warn('[FCM] Cannot show notification - user needs to grant permission');
        return;
      }
      
      const notificationTitle = payload.notification?.title || payload.data?.title || 'New Notification';
      const notificationBody = payload.notification?.body || payload.data?.body || 'You have a new notification';
      const notificationIcon = payload.notification?.icon || payload.data?.icon || '/icon-192x192.png';
      const clickUrl = payload.data?.url || payload.fcmOptions?.link || '/';
      
      try {
        // Use notificationId as tag to prevent duplicate notifications
        const notificationTag = payload.data?.tag || notificationId;

        console.log('[FCM] Creating foreground notification:', {
          title: notificationTitle,
          body: notificationBody,
          tag: notificationTag,
          id: notificationId
        });

        // IMPORTANT: Show the notification through the SERVICE WORKER registration
        // (not the page's `new Notification()` constructor).
        //
        // A notification created with `new Notification()` is owned by the page.
        // Once the tab/browser is closed, its JS onclick handler no longer exists,
        // so clicking it falls back to the browser default (opening the app at its
        // scope root -> home page). By using registration.showNotification(), the
        // click is handled by the persistent service worker's `notificationclick`
        // listener, which reliably opens the correct URL even after the page closes.
        const registration =
          (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ||
          (await navigator.serviceWorker.ready);

        if (registration) {
          await registration.showNotification(notificationTitle, {
            body: notificationBody,
            icon: notificationIcon,
            badge: notificationIcon,
            tag: notificationTag, // Use tag to replace previous notification with same tag
            data: {
              url: clickUrl,
              notificationId: notificationId,
              ...payload.data
            },
            requireInteraction: false,
            silent: false
          });

          // Mark this notification as shown
          shownNotificationIds.add(notificationId);

          // Clean up old notification IDs after 1 minute to prevent memory leak
          setTimeout(() => {
            shownNotificationIds.delete(notificationId);
          }, 60000);

          console.log('[FCM] ✅ Foreground notification displayed via service worker:', notificationTitle, 'ID:', notificationId);
        } else {
          console.warn('[FCM] No service worker registration available to show notification');
        }
        } catch (notificationError: any) {
          console.error('[FCM] ❌ Error creating notification:', notificationError);
          console.error('[FCM] Error details:', {
            message: notificationError?.message,
            name: notificationError?.name,
            stack: notificationError?.stack
          });
          
          // Try to show a fallback notification or log to console
          console.warn('[FCM] Notification could not be displayed. Check browser console for details.');
        }
    });

    if (unsubscribe) {
      foregroundListenerSetup = true;
      foregroundUnsubscribe = unsubscribe;
      console.log('[FCM] ✅ Foreground notification listener setup successfully');
      console.log('[FCM] Listener is ready to receive messages when page is visible');
    } else {
      foregroundListenerSetup = false;
      foregroundUnsubscribe = null;
      console.warn('[FCM] ⚠️ Foreground notification listener setup returned null');
    }
  } catch (error) {
    console.error('[FCM] ❌ Error setting up foreground notification listener:', error);
    foregroundListenerSetup = false; // Reset on error so we can retry
    foregroundUnsubscribe = null;
  }
};

/**
 * Validate and refresh FCM token if needed
 * Checks cookie for existing token, validates it, and generates new one if invalid
 * Note: accessToken is handled by axios defaults (cookies/headers)
 */
export const validateAndRefreshFCMToken = async (vendorData: vendorDataResponse | null): Promise<string | null> => {
  try {
    console.log('[FCM] Starting FCM token validation and refresh...');

    // Step 1: Check for existing token in cookie
    const existingToken = getFCMTokenCookie();
    
    if (existingToken) {
      console.log('[FCM] Found existing token in cookie, validating...');
      
      // Step 2: Validate existing token with backend
      const isValid = await checkFCMTokenValidity(existingToken);
      
      if (isValid) {
        console.log('[FCM] Existing token is valid, no need to generate new token');
        
        // IMPORTANT: Setup foreground listener even when token is valid
        // This ensures notifications work when app is open/active
        console.log('[FCM] Setting up foreground notification listener for valid token...');
        try {
          await setupForegroundNotificationListener();
          console.log('[FCM] ✅ Foreground listener setup completed for valid token');
        } catch (listenerError) {
          console.error('[FCM] ❌ Error setting up foreground listener for valid token:', listenerError);
          // Don't fail the whole process, but log the error
        }
        
        return existingToken;
      } else {
        console.log('[FCM] Existing token is invalid, will generate new token');
        // Remove invalid token from cookie
        removeFCMTokenCookie(vendorData);
      }
    } else {
      console.log('[FCM] No existing token found in cookie');
    }

    // Step 3: Generate new token (existing token was invalid or missing)
    const newToken = await generateAndRegisterFCMToken();
    
    if (newToken) {
      // Store new token in cookie
      setFCMTokenCookie(newToken, vendorData);
    }
    
    return newToken;
  } catch (error) {
    console.error('[FCM] Error in validateAndRefreshFCMToken:', error);
    return null;
  }
};

/**
 * Generate and register FCM token
 * This is the main function to call after user login
 * Note: accessToken is handled by axios defaults (cookies/headers)
 */
export const generateAndRegisterFCMToken = async (): Promise<string | null> => {
  try {
    console.log('[FCM] Starting FCM token generation and registration...');

    // Step 1: Request notification permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('[FCM] Notification permission not granted. Cannot generate FCM token.');
      return null;
    }

    // Step 2: Register service worker (REQUIRED for FCM token generation)
    const registration = await registerServiceWorker();
    if (!registration) {
      console.error('[FCM] Failed to register service worker');
      console.error('[FCM] FCM token generation requires a service worker file at public/firebase-messaging-sw.js');
      return null;
    }

    // Step 3: Initialize Firebase Messaging
    console.log('[FCM] Initializing Firebase Messaging...');
    const messaging = await initializeFirebaseMessaging();
    if (!messaging) {
      console.error('[FCM] Failed to initialize Firebase Messaging');
      return null;
    }
    console.log('[FCM] Firebase Messaging initialized successfully');

    // Step 3.5: Setup foreground message listener (for when app is open)
    // This handles notifications when user is actively viewing the page
    await setupForegroundNotificationListener();

    // Step 4: Get FCM token (force new token generation on each login)
    // If registration is null, we'll get token without service worker (foreground only)
    console.log('[FCM] Getting FCM token...');
    const token = await getFCMToken(registration || null, true); // true = force new token
    if (!token) {
      console.error('[FCM] Failed to get FCM token');
      return null;
    }

    console.log('[FCM] FCM token generated successfully:', token.substring(0, 50) + '...');

    // Step 6: Register token with backend (accessToken is handled by axios defaults)
    const registered = await registerFCMTokenWithBackend(token);
    if (!registered) {
      console.error('[FCM] Failed to register token with backend, but token was generated');
      // Still return token even if backend registration failed
      return token;
    }

    console.log('[FCM] FCM token generation and registration completed successfully');
    return token;
  } catch (error) {
    console.error('[FCM] Error in generateAndRegisterFCMToken:', error);
    return null;
  }
};

